import { ArrowRightOutlined } from '@ant-design/icons';
import {
  Avatar,
  Card,
  Col,
  Divider,
  Flex,
  Row,
  Skeleton,
  Space,
  Tabs,
  Typography,
} from 'antd';
import NoImage from 'assets/no-image.png';
import CustomFileList from 'components/CustomFileList';
import Tag from 'components/Tags';
import TaskInfo from 'components/TaskInfo';
import { useTranslation } from 'react-i18next';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import useFarmerGroup from 'screens/farmer-groups/hooks/useFarmerGroupListQuery';
import { getWhatsappStatus } from 'screens/stakeholders/helper';
import {
  PaddyBuyer,
  StakeholderStatus as StakeholderStatusType,
} from 'types/common';
import DateTime from 'utils/datetime';
import { getStakeholderTypeForCountry, renderDate } from 'utils/helpers';
import PbFarmerInvoiceList from '../farmer-invoice/PbFarmerInvoiceList';
import StakeholderStatus from '../StakeholderStatus';
import VerificationWarning from '../VerificationWarning';

type PaddyBuyerContentProps = {
  country: string;
  data: PaddyBuyer;
  onClickFarmerGroup: (id: string) => void;
  activeKey: string;
  setActiveKey: (key: string) => void;
};

const PaddyBuyerContent = ({
  country,
  data,
  onClickFarmerGroup,
  activeKey,
  setActiveKey,
}: PaddyBuyerContentProps) => {
  const { t } = useTranslation();
  const waStatus = getWhatsappStatus(data?.waVerifiedStatus);
  const { data: farmerGroupData, isLoading: isFarmerGroupLoading } =
    useFarmerGroup({
      country: data?.address?.country,
      paddyBuyer: data?.id,
    });
  const tabItems = [
    {
      key: '1',
      label: t('addFgForm.personalDetails'),
      children: activeKey === '1' && (
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col span={12}>
            <TaskInfo
              header={t('farmerDetailsPage.phoneNumber')}
              value={data?.phoneNumber}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('stakeholders.waVerification')}
              value={
                <p>
                  <Tag label={waStatus.label} type={waStatus.statusColor} />
                </p>
              }
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('stakeholders.seniorAgronomistEmail')}
              value={data?.agronomist?.email}
            />
          </Col>
          <Col span={12}>
            <Flex vertical gap={3}>
              <TaskInfo
                header={t('stakeholders.stakeholderStatus')}
                renderValue={() => (
                  <StakeholderStatus
                    status={data?.status as StakeholderStatusType}
                  />
                )}
              />
            </Flex>
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('farmerDetailsPage.nationalIdNumber')}
              value={data?.nationalIdNumber}
            />
          </Col>
          <Col span={12}>
            <TaskInfo header={t('farmerDetailsPage.nationalIdPicture')}>
              <CustomFileList
                images={data?.nationalIdDocs?.map((file) => file.url)}
              />
            </TaskInfo>
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('addFgForm.createdBy')}
              value={data?.createdBy}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('common.createdDate')}
              value={renderDate(data?.reportedDate)}
            />
          </Col>
          <Divider style={{ margin: '12px 0' }} />
          <Col span={24}>
            <TaskInfo
              header={t('farmerDetailsPage.address')}
              value={data?.address?.address}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('farmerDetailsPage.province')}
              value={data?.address?.stateOrProvince}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('farmerDetailsPage.district')}
              value={data?.address?.district}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('farmerDetailsPage.subDistrict')}
              value={data?.address?.subDistrict}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('farmerDetailsPage.village')}
              value={data?.address?.cityOrVillage}
            />
          </Col>
          <Divider style={{ margin: '12px 0' }} />
          <Col span={12}>
            <TaskInfo
              header={t('stakeholders.bankName')}
              value={data?.bankAccountInfos?.[0]?.paymentType?.name}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              header={t('payment.accNum')}
              value={data?.bankAccountInfos?.[0]?.accountNumber}
            />
          </Col>
          <Col span={24}>
            <TaskInfo
              header={t('stakeholders.accountHolderName')}
              value={data?.bankAccountInfos?.[0]?.accountHolderName}
            />
          </Col>
          <Divider style={{ margin: '12px 0' }} />
          <Col span={12}>
            <TaskInfo header={t('stakeholders.contractValidFrom')}>
              {renderDate(data?.contractStartDate, '-', 'DD MMMM, YYYY')}
            </TaskInfo>
          </Col>
          <Col span={12}>
            <TaskInfo header={t('stakeholders.contractValidUntil')}>
              {data?.contractStartDate
                ? renderDate(
                    DateTime.add(data?.contractStartDate, 1, 'year').toString(),
                    '-',
                    'DD MMMM, YYYY',
                  )
                : '-'}
            </TaskInfo>
          </Col>
          <Col span={12}>
            <TaskInfo header={t('stakeholders.firstPageContractPicture')}>
              <CustomFileList
                images={
                  data?.firstPageContract ? [data?.firstPageContract?.url] : []
                }
              />
            </TaskInfo>
          </Col>
          <Col span={12}>
            <TaskInfo header={t('stakeholders.signedContractPicture')}>
              <CustomFileList
                images={data?.contractDocs?.map((file) => file.url) ?? []}
              />
            </TaskInfo>
          </Col>
        </Row>
      ),
    },
    {
      key: '2',
      label: t('groupsPage.farmerGroups'),
      children: activeKey === '2' && (
        <Space direction="vertical" size={12} style={{ display: 'flex' }}>
          {isFarmerGroupLoading && (
            <Card className="farmer-card">
              <Skeleton active />
            </Card>
          )}
          {farmerGroupData?.data?.content?.map((farmerGroup) => (
            <Card
              className="farmer-card clickable"
              key={farmerGroup?.id}
              onClick={() => onClickFarmerGroup(farmerGroup?.id)}
            >
              <Row gutter={[8, 4]}>
                <Col span={24}>
                  <Flex justify="space-between">
                    <Typography.Title level={5}>
                      {farmerGroup?.name}
                    </Typography.Title>
                    <ArrowRightOutlined className="activity-widget--btn-view" />
                  </Flex>
                </Col>
                <Col span={12}>
                  <TaskInfo
                    wrapperClass="no-padding"
                    header={t('groupDetailPage.leaderName')}
                    value={farmerGroup?.groupLeader?.name}
                  />
                </Col>
                <Col span={12}>
                  <TaskInfo
                    wrapperClass="no-padding"
                    header={t('addFgForm.subDistrict')}
                    value={farmerGroup?.address?.subDistrict}
                  />
                </Col>
                <Col span={12}>
                  <TaskInfo
                    wrapperClass="no-padding"
                    header={t('groupDetailPage.totalFarmers')}
                    value={farmerGroup?.totalFarmers}
                  />
                </Col>
                <Col span={12}>
                  <TaskInfo
                    wrapperClass="no-padding"
                    header={t('groupDetailPage.ownedHectares')}
                    value={farmerGroup?.cubeOwnedHectares}
                  />
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      ),
    },
    {
      key: '3',
      label: t('stakeholders.farmerInvoices'),
      children: activeKey === '3' && <PbFarmerInvoiceList pbId={data?.id} />,
    },
  ];
  return (
    <>
      <Card size="small">
        <Flex gap={12} align="center">
          {data?.documentList?.[0]?.url ? (
            <PhotoProvider>
              <PhotoView src={data.documentList[0].url}>
                <img
                  src={data.documentList[0].url}
                  alt={t('stakeholders.paddyBuyerProfile')}
                  className="img-fluid me-2 farmer-profile"
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <Avatar src={NoImage} size={44} />
          )}
          <div>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {data?.name}
            </Typography.Title>
            <Typography className="info-label">
              {getStakeholderTypeForCountry(country, 'PB')}
            </Typography>
          </div>
        </Flex>
      </Card>
      <VerificationWarning
        data={data}
        type="PADDY_BUYER"
        style={{ marginTop: 16 }}
      />
      <Tabs
        className="stakeholder-tabs"
        items={tabItems}
        defaultActiveKey="1"
        activeKey={activeKey}
        onTabClick={setActiveKey}
      />
    </>
  );
};

export default PaddyBuyerContent;
